import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  BookabilityDecision,
  type BookabilityStatusItem,
} from "@src/appV2/OpenShifts/ShiftAction/types";
import { useCallback, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { LoadingButton } from "../../components/LoadingButton";
import { type ShiftBlockWithMappedDetails } from "./api/useFetchPaginatedShiftBlocks";
import { useRequestToBlockShiftBlock } from "./api/useRequestToBookShiftBlock";
import { RequestToBookShiftBlockBottomSheet } from "./RequestToBookShiftBlockBottomSheet";
import { BookShiftBlockErrors, SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  disabled: boolean;
  facilityId: string;
  agentId: string;
  shiftBlockId: string;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  shiftWithBlockedStatus?: BookabilityStatusItem;
}

export function RequestToBookShiftBlockButton(props: Props) {
  const { disabled, facilityId, agentId, shiftBlockId, shifts, shiftWithBlockedStatus } = props;
  const requestToBookShiftBlockState = useModalState();
  const { showErrorToast, showSuccessToast } = useToast();
  const { path: currentRouterUrl } = useRouteMatch();
  const [isRequestSubmitting, setIsRequestSubmitting] = useState(false);
  const {
    mutateAsync: mutateRequestToBookShiftBlock,
    isLoading: isLoadingRequestToBookShiftBlock,
  } = useRequestToBlockShiftBlock({
    shiftBlockId,
  });
  const isRequestInFlight = isRequestSubmitting || isLoadingRequestToBookShiftBlock;

  const handleRequestBlockClicked = useCallback(() => {
    if (isRequestInFlight) {
      return;
    }

    if (
      isDefined(shiftWithBlockedStatus) &&
      shiftWithBlockedStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED
    ) {
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.REQUEST_THIS_BLOCK_TAPPED,
        shiftBlockId,
        facilityId,
        unmetCriteria: shiftWithBlockedStatus.attributes.bookability.unmetCriteria,
        location: currentRouterUrl,
      });
      // There can be multiple reasons for the shift block booking being blocked, however for the purposes of showing the error message to the user, we are only showing the first unmet criteria.
      showErrorToast(
        BookShiftBlockErrors[shiftWithBlockedStatus.attributes.bookability.unmetCriteria[0]] ??
          "Error: You cannot request to book this block."
      );
    } else {
      logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
        action: SHIFT_BLOCK_ACTIONS.REQUEST_THIS_BLOCK_TAPPED,
        shiftBlockId,
        facilityId,
        location: currentRouterUrl,
      });
      requestToBookShiftBlockState.openModal();
    }
  }, [
    currentRouterUrl,
    facilityId,
    isRequestInFlight,
    requestToBookShiftBlockState,
    shiftBlockId,
    shiftWithBlockedStatus,
    showErrorToast,
  ]);

  const handleConfirmRequest = useCallback(async () => {
    if (isRequestInFlight) {
      return;
    }

    setIsRequestSubmitting(true);

    try {
      await mutateRequestToBookShiftBlock({
        data: {
          type: "shift-block-booking-request",
          attributes: {
            agentId,
          },
        },
      });
      showSuccessToast("We've received your request. We'll be in touch with you shortly!");
    } catch {
      showErrorToast("Something went wrong while requesting to book the shift block.");
    } finally {
      setIsRequestSubmitting(false);
    }
  }, [agentId, isRequestInFlight, mutateRequestToBookShiftBlock, showErrorToast, showSuccessToast]);

  return (
    <>
      <LoadingButton
        fullWidth
        variant="contained"
        size="medium"
        disabled={disabled || isRequestInFlight}
        isLoading={isRequestInFlight}
        onClick={handleRequestBlockClicked}
      >
        Request This Block
      </LoadingButton>

      <RequestToBookShiftBlockBottomSheet
        modalState={requestToBookShiftBlockState}
        shifts={shifts}
        shiftBlockId={shiftBlockId}
        facilityId={facilityId}
        onConfirmRequest={() => {
          void handleConfirmRequest();
        }}
      />
    </>
  );
}
